import type { CredentialResponse } from '@react-oauth/google';

type PromptMomentNotification = {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getDismissedReason?: () => string;
  getNotDisplayedReason?: () => string;
  getSkippedReason?: () => string;
};

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    hosted_domain?: string;
    use_fedcm_for_prompt?: boolean;
  }) => void;
  prompt: (momentListener?: (notification: PromptMomentNotification) => void) => void;
  cancel: () => void;
};

function getGoogleAccountsId(): GoogleAccountsId | null {
  const google = (window as Window & { google?: { accounts?: { id?: GoogleAccountsId } } }).google;
  return google?.accounts?.id ?? null;
}

const REFRESH_TIMEOUT_MS = 12_000;

/**
 * Request a fresh Google ID token via GIS One Tap with auto-select.
 * Succeeds silently when the user still has an active Google session for this client.
 */
export function requestSilentGoogleCredential(
  clientId: string,
  options?: { hostedDomain?: string },
): Promise<CredentialResponse | null> {
  const accountsId = getGoogleAccountsId();
  if (!accountsId) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: CredentialResponse | null) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(value);
    };

    const timeoutId = window.setTimeout(() => {
      try {
        accountsId.cancel();
      } catch {
        // ignore
      }
      finish(null);
    }, REFRESH_TIMEOUT_MS);

    accountsId.initialize({
      client_id: clientId,
      auto_select: true,
      cancel_on_tap_outside: false,
      use_fedcm_for_prompt: true,
      hosted_domain: options?.hostedDomain,
      callback: (response) => {
        if (response?.credential) {
          finish(response);
        } else {
          finish(null);
        }
      },
    });

    accountsId.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        finish(null);
        return;
      }
      if (notification.isDismissedMoment()) {
        const reason = notification.getDismissedReason?.();
        // "credential_returned" means success; callback will settle. Other dismissals fail.
        if (reason && reason !== 'credential_returned') {
          finish(null);
        }
      }
    });
  });
}
